// Owns the rapid-association block state: which of the three views the question is drawn as, the
// chart's mode and zoom, the sort order, and which words are open in the stacked view. Seeded from the question's params and
// re-seeded whenever any of them changes outside the block.
//
// The block answers for its whole subtree rather than for itself alone: in the stacked view every
// word is a batch owning report params of its own, and each hands its api up as it mounts. So the
// chart-mode normalization and the export payload are gathered here, across this block and every
// batch under it, and the shell only forwards what it is given.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getModelValue, getTrait } from '@aytm/app/stats/lib/stat-page'
import {
  calcLabelClickTarget,
  isRType as calcIsRType,
  isTraitType as calcIsTraitType
} from '@aytm/app/stats/page/components/lib/utils'
import { calcDisplayRange } from '@aytm/app/stats/page/question-views/oe/lib/display-range'
import {
  calcChartParamsSection,
  calcCollapseSection,
  calcDefaultChartMode,
  calcSortParamsSection,
  calcZoomSection,
  calcViewModeSection,
  hasZoom,
  resolveChartMode,
  resolveSortOrder,
  resolveUncollapsed,
  resolveViewMode
} from '@aytm/app/stats/page/question-views/ra/lib/report-params'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { decodeZoomParam } from '@aytm/app/stats/page/question-views/components/lib/zoom-param'

export default function useRapidAssociationState({
  params,
  paramsRevision,
  questionKey,
  type,
  format,
  answer,
  answerVariants,
  batches,
  questionChartMode,
  defaultSortOrder,
  labelTarget,
  onReady,
  onStateChange,
  onBatchParamsChange
}) {
  const context = { key: questionKey, type, format, answer, questionChartMode }

  const contextRef = useRef()
  contextRef.current = { ...context, defaultSortOrder, answerVariants, batches }

  const seed = useCallback(source => {
    const { format, defaultSortOrder } = contextRef.current

    return {
      viewMode: resolveViewMode(source),
      chartMode: resolveChartMode(source, format),
      zoomLevel: decodeZoomParam(source.z),
      sortOrder: resolveSortOrder(source.s, defaultSortOrder),
      uncollapsed: resolveUncollapsed(source.cb)
    }
    // everything it reads comes off the ref, so it never re-binds
  }, [])

  const [state, setState] = useState(() => seed(params || {}))

  const snapshot = useRef()
  snapshot.current = { state, context, labelTarget }

  // the mounted chart knows the axis it is showing; the exporters ask the state for it
  const chartApiRef = useRef(null)
  const onChartReady = useCallback(api => {
    chartApiRef.current = api
  }, [])

  // Every batch of the stacked view hands its api up as it mounts; the block answers for them as
  // well as for itself, so the exporters reach the whole subtree through one call.
  const batchApis = useRef({})
  const onBatchReady = useCallback((position, batchApi, meta) => {
    if (null == batchApi) delete batchApis.current[position]
    else batchApis.current[position] = { api: batchApi, meta: meta || {} }
  }, [])

  // A word announces its own changes the way any block does; this block is what writes them down,
  // so the announcement is forwarded with the key the params hang off and the section the word
  // resolved -- the reason alone says a change happened, not what it was.
  const announceRefs = useRef()
  announceRefs.current = { onBatchParamsChange }

  const onBatchStateChange = useCallback((position, key, reason) => {
    if ('state' !== reason) return

    const entry = batchApis.current[position]
    if (!entry) return

    announceRefs.current.onBatchParamsChange?.(key, entry.api.getReportParamsSection?.() || {})
  }, [])

  // every batch, paired with whatever it answers -- the ones that answer nothing drop out
  const gather = useCallback(
    read =>
      Object.entries(batchApis.current).reduce((acc, [position, entry]) => {
        const value = read(entry.api, position, entry.meta)

        return null == value ? acc : acc.concat([[position, value]])
      }, []),
    []
  )

  const pendingReason = useRef('seed')

  const onModelChange = useCallback(
    (change = {}) =>
      setState(current => {
        if (Object.entries(change).every(([key, value]) => current[key] === value)) return current

        pendingReason.current = 'state'

        return { ...current, ...change }
      }),
    []
  )

  const api = useMemo(() => {
    // the mode the chart opens on once the question and global defaults are applied -- what the
    // exporters draw by, as opposed to `chartMode`, which is null until the researcher picks one
    const resolveExportChartMode = () => {
      const { state, context } = snapshot.current

      return calcDefaultChartMode({
        chartMode: state.chartMode || context.questionChartMode,
        questionChartMode: context.questionChartMode,
        globalChartMode: getModelValue('chartMode'),
        viewMode: state.viewMode,
        percentsOnly: true === context.format?.percentsOnly
      })
    }

    // the axis the chart is showing. Only the bar view has one to describe. The rendered chart
    // knows it, zoom included; the maths is the fallback for a chart that has not drawn yet.
    const resolveDisplayRange = (chartMode, zoomLevel) => {
      const { state, context } = snapshot.current

      if (!hasZoom(state.viewMode)) return null
      if (getTrait() && calcIsTraitType(context)) return null

      const extremes = chartApiRef.current?.getAxisExtremes?.()
      if (null != extremes) return extremes

      return calcDisplayRange({
        chartMode,
        zoomLevel: zoomLevel || state.zoomLevel || getModelValue('zoomLevel'),
        globalChartMode: getModelValue('chartMode'),
        format: context.format,
        answer: context.answer,
        answerVariants: contextRef.current.answerVariants,
        rType: calcIsRType(context) ? getModelValue('rType') : 'cases',
        confidenceLevels: PageGlobals.CONFIDENCE_LEVELS
      })
    }

    return {
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => {
        const { state, context } = snapshot.current

        return {
          ...calcViewModeSection(state.viewMode),
          ...calcZoomSection(state.zoomLevel, getModelValue('zoomLevel')),
          // a view with no axis drops the zoom with it, so the chart section is applied over it
          ...calcChartParamsSection({
            chartMode: state.chartMode,
            questionChartMode: context.questionChartMode,
            format: context.format,
            viewMode: state.viewMode
          }),
          ...calcSortParamsSection(state.sortOrder, contextRef.current.defaultSortOrder),
          ...calcCollapseSection(state.uncollapsed)
        }
      },
      // pptx / summary bridge: what the block is showing, with every open batch spelled out
      getExportData: () => {
        const { state } = snapshot.current
        const chartModeResolved = resolveExportChartMode()

        return {
          viewMode: state.viewMode,
          chartMode: state.chartMode,
          chartModeResolved,
          displayRange: resolveDisplayRange(chartModeResolved, null),
          sortOrder: state.sortOrder,
          uncollapsed: state.uncollapsed,
          items: gather((batchApi, position, meta) => ({ ...meta, ...(batchApi.getExportData?.() || {}) })).map(
            ([position, model]) => ({
              uid: model.uid ?? Number(position),
              position: Number(position),
              model
            })
          )
        }
      },
      getDisplayRange: resolveDisplayRange,
      // The word a clicked row stands for. A row inside a batch belongs to that batch's
      // sub-question, so the batch it came from scopes the filter; a row of the question's own list
      // has none and falls to whatever the shell resolved.
      getLabelClickTarget: (position, batchPosition, answerVariants) => {
        const batch = (contextRef.current.batches || []).find(item => item && item.position == batchPosition)

        return calcLabelClickTarget({
          ...snapshot.current.labelTarget,
          ...(null == batchPosition ? {} : { batch: batch || { position: batchPosition } }),
          // a sentiments or tags row names its own variant, and only that list knows the tag
          answerVariants: answerVariants?.length
            ? answerVariants
            : batch?.answerVariants || contextRef.current.answerVariants,
          position
        })
      },
      update: onModelChange
    }
  }, [gather, onModelChange])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // A param moved outside the block (another view of the question, the URL, an undo): re-seed.
  //
  // Keyed on the revision the shell bumps per host render, not on the contents of the params bag.
  // A host-driven render is a fresh read of the live model; a re-render React starts for itself
  // carries the revision it already had. Keying on the contents missed an undo restoring a value
  // the block had already moved away from locally, because the block's own write is silent and the
  // string it was handed had never changed.
  useEffect(() => {
    pendingReason.current = 'seed'
    setState(seed(params ?? {}))
  }, [params, paramsRevision, seed])

  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    if (reason) onStateChangeRef.current?.(reason)
  }, [state])

  // Picking a view drops the chart mode with it: what the words are plotted as is the view's to
  // decide, and a mode chosen for one view means nothing in another. A view with no axis drops the
  // zoom too.
  const onViewModeChange = useCallback(
    viewMode =>
      onModelChange({
        viewMode,
        chartMode: null,
        zoomLevel: hasZoom(viewMode) ? snapshot.current.state.zoomLevel : null
      }),
    [onModelChange]
  )

  const onChartModeChange = useCallback(chartMode => onModelChange({ chartMode }), [onModelChange])

  // the chart calls it with its own key, which the block already knows
  const onZoomChange = useCallback((key, zoomLevel) => onModelChange({ zoomLevel }), [onModelChange])

  const onSortChange = useCallback(sortOrder => onModelChange({ sortOrder }), [onModelChange])

  // Opening or closing every word at once is a set operation on the ones there are, not a toggle of
  // what is currently open.
  const onToggleAll = useCallback(
    collapse => {
      const positions = collapse
        ? []
        : (contextRef.current.answerVariants || [])
            .filter(Boolean)
            .map(av => String(av.position))
            .sort()

      onModelChange({ uncollapsed: positions.join('.') })
    },
    [onModelChange]
  )

  const onToggle = useCallback(
    (position, collapsed) => {
      const open = new Set(snapshot.current.state.uncollapsed.split('.').filter(Boolean))

      if (collapsed) open.delete(String(position))
      else open.add(String(position))

      onModelChange({ uncollapsed: [...open].sort().join('.') })
    },
    [onModelChange]
  )

  return {
    ...state,
    onChartReady,
    onViewModeChange,
    onChartModeChange,
    onZoomChange,
    onSortChange,
    onBatchReady,
    onBatchStateChange,
    onToggle,
    onToggleAll,
    onModelChange
  }
}
